import {
  CURRENCY_CODE_BY_STYLE,
  countryOf,
  deriveAddressStyle,
  deriveCurrencyStyle,
  deriveDateStyle,
  deriveDateTimeStyle,
  deriveFirstDayOfWeek,
  deriveNumberStyle,
  derivePhoneStyle,
  deriveTimeStyle,
  deriveTimeZone,
  deriveUnitSystem,
  deriveUnits,
  normaliseLocale,
  UNITS_BY_SYSTEM,
} from './defaults';
import {
  CLOCK_BY_LIBRARY,
  DATE_STYLE_BY_LIBRARY,
  DIMENSION_BY_KIND,
  unitCodeOfLibraryUnit,
} from './library';
import {
  CURRENCY_CODES,
  type CurrencyCode,
  REGIONAL_DEFAULTS,
} from './regional';
import { signatureOf } from './signature';
import {
  type FormatFamily,
  ISSUE_REASONS,
  type MeasurementKind,
  type PreferenceIssue,
  type PreferenceValues,
  type ResolvedFormats,
  type ResolvedPreferences,
  type ResolvedUnits,
  STYLE_CODES,
  UNIT_CODES,
  UNIT_SYSTEMS,
  type UnitCodeFor,
  type UnitSystem,
} from './types';
import {
  type CodeProblem,
  describeValue,
  foldCode,
  type Reading,
  readCode,
  readString,
  readTimeZone,
} from './validate';
import type {
  PreferenceInput,
  WireEffectivePreferences,
  WireFormats,
  WireMeasurements,
  WireUnits,
} from './wire';

/**
 * The single entry point that turns a `/me` payload into preferences the rest of the system reads.
 *
 * Four levels of precedence, highest first:
 *
 * 1. an explicit per-field code on `effectiveUserPreferences`
 * 2. the `unitSystem` preset it sits beside, for measurements
 * 3. the locale, via `Intl`
 * 4. `en-US`
 *
 * Level 4 needs no branch of its own: an absent or unparsable locale becomes `REGIONAL_DEFAULTS.locale`, and
 * level 3 always answers for whatever locale it is given.
 *
 * Level 1 has two possible sources, because profile-api ships two preference vocabularies at once.
 * The `csp-commonlib-internationalization` one — flat `units`, `unitSystem`, `dateStyle` — wins
 * wherever it answers; the deprecated `measurements`/`formats` blocks answer for the fields it has
 * no successor for, and for payloads predating it entirely. `library.ts` holds the translation.
 */

export type PreferenceResolution = {
  readonly preferences: ResolvedPreferences;
  readonly issues: readonly PreferenceIssue[];
};

/**
 * The countries `UserPreferenceFactory` actually has settings for.
 *
 * Applies to the deprecated `measurements`/`formats` blocks only. The library vocabulary resolves
 * against `LocaleDefaults`, whose country table has no US fallthrough and answers metric for
 * Canada, so a payload speaking it is trusted outright — see {@link carriesLibraryVocabulary}.
 *
 * It resolves against a table holding `en-US` and `es-MX`, and falls through to the US bundle for
 * everything else. So a Canadian user is sent `MILES` and `F` — not because anyone chose imperial
 * for Canada, but because Canada is missing. Today the frontend gives those users metric through
 * `UNITS_CONFIGURATION`, and adopting the backend answer verbatim would be a visible regression on
 * the first screen that renders a distance.
 *
 * Outside these two countries, backend measurement and format codes are treated as underived and
 * the locale answers instead.
 *
 * Written as the countries the backend *knows* rather than the ones it is missing, because the
 * second list is unbounded: Canada is the only gap among the four locales the app supports today,
 * but the fallthrough hits every country without a row. Naming the gaps would leave the fifth
 * locale silently trusting a US default. A locale carrying no region cannot be matched to a row at
 * all, so it is not trusted either.
 *
 * The whole mechanism is safe only while nothing can set an override, because a fallen-through
 * `MILES` is indistinguishable on the wire from a deliberately chosen one. The profile preferences
 * screen does not exist yet, so every code currently arriving is backend-derived. Add a country
 * when the backend adds its row; delete this and trust the payload outright once that screen ships.
 */
export const COUNTRIES_WITH_BACKEND_DEFAULTS = Object.freeze([
  'US',
  'MX',
] as const);

const BACKEND_DEFAULT_COUNTRY_SET: ReadonlySet<string> = new Set(
  COUNTRIES_WITH_BACKEND_DEFAULTS
);

/**
 * The deprecated block's unit system, where `IMPERIAL` means the United States.
 *
 * The one place the two vocabularies use the same word for different things. The older enum has
 * two members and calls the US bundle imperial, in the colloquial North American sense; the
 * library has three and reserves `IMPERIAL` for the UK hybrid. Passing the string through
 * unexamined would put a US reader on litres and Celsius, which is why this is a table and not a
 * cast.
 */
const SYSTEM_BY_DEPRECATED_CODE: Readonly<Record<string, UnitSystem>> = {
  METRIC: 'METRIC',
  IMPERIAL: 'US_CUSTOMARY',
};

const collect = (
  issues: PreferenceIssue[],
  problem: CodeProblem | undefined,
  resolvedTo: string
): void => {
  if (problem) issues.push({ ...problem, resolvedTo });
};

/**
 * True when a wire value names a code rather than standing in for one.
 *
 * An unset column serialises to `''` as readily as it does to `null`, and `readCode` refuses both,
 * so counting an empty string here would report a discarded preference the user never expressed.
 */
const namesACode = (raw: unknown): boolean =>
  typeof raw === 'string' && raw.trim() !== '';

/** True when a measurements or formats block actually names a code, not merely exists as `{}`. */
const blockCarriesACode = (
  measurements: WireMeasurements,
  formats: WireFormats
): boolean => {
  if (measurements) {
    if (namesACode(measurements.unitSystem)) return true;
    for (const kind of Object.keys(UNIT_CODES) as MeasurementKind[]) {
      if (namesACode(measurements[kind]?.unitCode)) return true;
    }
  }

  if (formats) {
    for (const family of Object.keys(STYLE_CODES) as FormatFamily[]) {
      if (family === 'firstDayOfWeek') {
        if (namesACode(formats.firstDayOfWeek?.firstDayOfWeekCode)) return true;
      } else if (namesACode(formats[family]?.styleCode)) {
        return true;
      }
    }
  }

  return false;
};

/**
 * True when the payload speaks the library vocabulary at all.
 *
 * Decided once for the whole payload rather than per field, because the two vocabularies imply
 * different trust: the library's country table resolves every country, while the deprecated
 * blocks fall through to US for anything but US and MX. A payload carrying any library field was
 * produced by the newer resolver, so its answers are derived rather than fallen into.
 *
 * `units` is checked for a populated map rather than mere presence, since `{}` is what a reader
 * with no renderable dimensions serialises to and says nothing about which resolver ran.
 */
const carriesLibraryVocabulary = (
  effective: WireEffectivePreferences
): boolean => {
  if (!effective) return false;

  const scalars = [
    effective.unitSystem,
    effective.dateStyle,
    effective.clockConvention,
    effective.firstDayOfWeek,
    effective.currencyCode,
  ];

  if (scalars.some(namesACode)) return true;

  return Boolean(
    effective.units && Object.values(effective.units).some(namesACode)
  );
};

/**
 * One kind's unit from the library's `units` map.
 *
 * Returns a reading, not a collected issue: the caller knows what actually won (library, deprecated
 * block, or locale) and only then records `resolvedTo`.
 *
 * Absence is the normal case rather than a fault: the backend omits a dimension it has no unit for,
 * and `RATIO` and `DURATION` arrive for every reader while matching no kind here. Reporting those
 * would put two diagnostics on every page load and train everyone to ignore the channel.
 *
 * A dimension that *is* named but carries a unit this package cannot render is a different matter.
 */
const readLibraryUnit = <K extends MeasurementKind>(
  units: WireUnits,
  kind: K
): Reading<UnitCodeFor<K>> => {
  if (!units) return {};

  const dimension = DIMENSION_BY_KIND[kind];
  const raw = units[dimension];
  if (raw === undefined || raw === null) return {};

  const field = `effectiveUserPreferences.units.${dimension}`;

  if (typeof raw !== 'string') {
    return {
      problem: {
        field,
        received: describeValue(raw),
        reason: ISSUE_REASONS.MALFORMED_VALUE,
      },
    };
  }

  if (!raw.trim()) return {};

  const code = unitCodeOfLibraryUnit(kind, foldCode(raw));
  if (code) return { value: code };

  return {
    problem: {
      field,
      received: describeValue(raw),
      reason: ISSUE_REASONS.UNRECOGNISED_CODE,
    },
  };
};

/**
 * A scalar translated through a crosswalk, reported when it names something the table lacks.
 *
 * Serves the two library enums whose spelling differs from ours — `dateStyle`, `clockConvention` —
 * and the deprecated block's `unitSystem`, whose `IMPERIAL` needs redirecting. All three are small
 * closed enums on the backend, so an unrecognised value means it has grown a member we have not
 * seen rather than that a user typed something.
 *
 * Returns a reading, not a collected issue: the caller knows what actually won (library, deprecated
 * block, or locale) and only then records `resolvedTo`.
 */
const readMappedCode = <T extends string>(
  table: Readonly<Record<string, T>>,
  raw: unknown,
  field: string
): Reading<T> => {
  if (raw === undefined || raw === null) return {};

  if (typeof raw !== 'string') {
    return {
      problem: {
        field,
        received: describeValue(raw),
        reason: ISSUE_REASONS.MALFORMED_VALUE,
      },
    };
  }

  if (!raw.trim()) return {};

  const mapped = table[foldCode(raw)];
  if (mapped) return { value: mapped };

  return {
    problem: {
      field,
      received: describeValue(raw),
      reason: ISSUE_REASONS.UNRECOGNISED_CODE,
    },
  };
};

/**
 * A library scalar whose spelling already matches one of our codes — `firstDayOfWeek`, `unitSystem`
 * and `currencyCode`, the last two only because this package adopted the library's names.
 *
 * Needs no crosswalk, but still needs narrowing: `firstDayOfWeek` is a `DayOfWeek` on the wire, so
 * its domain is all seven days against the three this package models, and `currencyCode` is any
 * ISO 4217 code against the three markets it serves. Both reject the excess rather than widening.
 */
const readLibraryCode = <T extends string>(
  codes: readonly T[],
  raw: unknown,
  field: string,
  fallback: T,
  issues: PreferenceIssue[]
): T | undefined => {
  if (raw === undefined || raw === null) return undefined;
  if (typeof raw === 'string' && !raw.trim()) return undefined;

  const { value, problem } = readCode(codes, raw, field);
  collect(issues, problem, value ?? fallback);
  return value;
};

/**
 * Picks between two wire fields holding the same value, keeping the path the value came from so a
 * diagnostic names the field that actually carried the problem.
 *
 * The legacy block is passed first throughout, because every payload has it. The mirror on
 * `effectiveUserPreferences` covers a payload carrying only the library shape.
 */
const mirrored = (
  primary: unknown,
  primaryField: string,
  secondary: unknown,
  secondaryField: string
): { readonly raw: unknown; readonly field: string } =>
  !namesACode(primary) && namesACode(secondary)
    ? { raw: secondary, field: secondaryField }
    : { raw: primary, field: primaryField };

const resolveLocale = (
  input: PreferenceInput,
  issues: PreferenceIssue[]
): string => {
  const { raw, field } = mirrored(
    input?.userPreferences?.locale,
    'userPreferences.locale',
    input?.effectiveUserPreferences?.locale,
    'effectiveUserPreferences.locale'
  );

  const { value, problem } = readString(raw, field);
  collect(issues, problem, REGIONAL_DEFAULTS.locale);

  const normalised = normaliseLocale(value);
  if (value && !normalised) {
    issues.push({
      field,
      received: describeValue(value),
      reason: ISSUE_REASONS.UNRECOGNISED_CODE,
      resolvedTo: REGIONAL_DEFAULTS.locale,
    });
  }

  return normalised ?? REGIONAL_DEFAULTS.locale;
};

const resolveTimeZone = (
  input: PreferenceInput,
  issues: PreferenceIssue[]
): string => {
  const fallback = deriveTimeZone();
  const { raw, field } = mirrored(
    input?.userPreferences?.timeZone,
    'userPreferences.timeZone',
    input?.effectiveUserPreferences?.timeZone,
    'effectiveUserPreferences.timeZone'
  );

  const { value, problem } = readTimeZone(raw, field);
  collect(issues, problem, fallback);

  return value ?? fallback;
};

const resolveUnits = (
  wire: WireMeasurements,
  libraryUnits: WireUnits,
  locale: string,
  unitSystem: UnitSystem | undefined,
  trustBackend: boolean,
  issues: PreferenceIssue[]
): ResolvedUnits => {
  const localeUnits = deriveUnits(locale);
  const presetUnits = unitSystem ? UNITS_BY_SYSTEM[unitSystem] : undefined;
  const kinds = Object.keys(UNIT_CODES) as MeasurementKind[];

  // Each kind narrows to its own code union, which a single loop cannot express; the entries are
  // built through a widened record and read back through the mapped type.
  const resolved = {} as Record<MeasurementKind, string>;

  for (const kind of kinds) {
    const fallback = presetUnits?.[kind] ?? localeUnits[kind];
    const deprecatedField = `effectiveUserPreferences.measurements.${kind}.unitCode`;

    // The library map is read unconditionally: its answers come from a country table that resolves
    // every country, so the gate the deprecated block needs does not apply to it.
    const { value: libraryUnit, problem: libraryUnitProblem } = readLibraryUnit(
      libraryUnits,
      kind
    );
    let explicit: string | undefined = libraryUnit;
    let explicitField = `effectiveUserPreferences.units.${DIMENSION_BY_KIND[kind]}`;

    if (!explicit) {
      explicitField = deprecatedField;

      const codes = UNIT_CODES[kind] as readonly UnitCodeFor<typeof kind>[];
      const { value, problem } = readCode(
        codes,
        wire?.[kind]?.unitCode,
        deprecatedField
      );

      explicit = trustBackend ? value : undefined;

      // A discarded block is already reported as BACKEND_HAS_NO_LOCALE_DEFAULTS; a per-field
      // UNRECOGNISED_CODE would imply this code is why the locale default won, which it is not.
      if (trustBackend) collect(issues, problem, explicit ?? fallback);
    }

    const chosen = explicit ?? fallback;
    collect(issues, libraryUnitProblem, chosen);

    if (explicit && presetUnits && explicit !== presetUnits[kind]) {
      issues.push({
        field: explicitField,
        received: explicit,
        reason: ISSUE_REASONS.UNIT_SYSTEM_MISMATCH,
        resolvedTo: explicit,
      });
    }

    resolved[kind] = chosen;
  }

  return resolved as ResolvedUnits;
};

const resolveFormats = (
  wire: WireFormats,
  effective: WireEffectivePreferences,
  locale: string,
  trustBackend: boolean,
  issues: PreferenceIssue[]
): ResolvedFormats => {
  const read = <T extends string>(
    codes: readonly T[],
    raw: unknown,
    field: string,
    fallback: T
  ): T => {
    const { value, problem } = readCode(codes, raw, field);
    const chosen = (trustBackend ? value : undefined) ?? fallback;
    if (trustBackend) collect(issues, problem, chosen);
    return chosen;
  };

  const at = (family: string) => `effectiveUserPreferences.formats.${family}`;

  const localeDate = deriveDateStyle(locale);
  const { value: libraryDate, problem: libraryDateProblem } = readMappedCode(
    DATE_STYLE_BY_LIBRARY,
    effective?.dateStyle,
    'effectiveUserPreferences.dateStyle'
  );
  const date =
    libraryDate ??
    read(
      STYLE_CODES.date,
      wire?.date?.styleCode,
      `${at('date')}.styleCode`,
      localeDate
    );
  collect(issues, libraryDateProblem, date);

  const localeTime = deriveTimeStyle(locale);
  const { value: libraryTime, problem: libraryTimeProblem } = readMappedCode(
    CLOCK_BY_LIBRARY,
    effective?.clockConvention,
    'effectiveUserPreferences.clockConvention'
  );
  const time =
    libraryTime ??
    read(
      STYLE_CODES.time,
      wire?.time?.styleCode,
      `${at('time')}.styleCode`,
      localeTime
    );
  collect(issues, libraryTimeProblem, time);

  // Composed from the resolved pair rather than the locale, so an explicitly chosen date order or
  // clock still governs the combined style.
  const composedDateTime = deriveDateTimeStyle(date, time);

  // The library has no combined style, and the deprecated one restates the two fields above. Where
  // the library answered either, it owns the combination too, rather than letting a stale
  // `MDY_12H` from the older block contradict a date order the newer one just set.
  const dateTime =
    libraryDate || libraryTime
      ? composedDateTime
      : read(
          STYLE_CODES.dateTime,
          wire?.dateTime?.styleCode,
          `${at('dateTime')}.styleCode`,
          composedDateTime
        );

  // Currency *style* — symbol placement and spacing — has no library successor by design, because
  // the library treats that as a rendering concern. Only the ISO code crossed over.
  const currency = read(
    STYLE_CODES.currency,
    wire?.currency?.styleCode,
    `${at('currency')}.styleCode`,
    deriveCurrencyStyle(locale)
  );
  const styleCurrencyCode = CURRENCY_CODE_BY_STYLE[currency];
  const localeFirstDay = deriveFirstDayOfWeek(locale);

  return {
    date,
    time,
    dateTime,
    currency,
    currencyCode:
      readLibraryCode<CurrencyCode>(
        CURRENCY_CODES,
        effective?.currencyCode,
        'effectiveUserPreferences.currencyCode',
        styleCurrencyCode,
        issues
      ) ?? styleCurrencyCode,
    phone: read(
      STYLE_CODES.phone,
      wire?.phone?.styleCode,
      `${at('phone')}.styleCode`,
      derivePhoneStyle(locale)
    ),
    number: read(
      STYLE_CODES.number,
      wire?.number?.styleCode,
      `${at('number')}.styleCode`,
      deriveNumberStyle()
    ),
    address: read(
      STYLE_CODES.address,
      wire?.address?.styleCode,
      `${at('address')}.styleCode`,
      deriveAddressStyle(locale)
    ),
    firstDayOfWeek:
      readLibraryCode(
        STYLE_CODES.firstDayOfWeek,
        effective?.firstDayOfWeek,
        'effectiveUserPreferences.firstDayOfWeek',
        localeFirstDay,
        issues
      ) ??
      read(
        STYLE_CODES.firstDayOfWeek,
        wire?.firstDayOfWeek?.firstDayOfWeekCode,
        `${at('firstDayOfWeek')}.firstDayOfWeekCode`,
        localeFirstDay
      ),
  };
};

/**
 * Resolves a `/me` payload into one immutable preference set.
 *
 * Never throws. Anything unusable is replaced and described in `issues`, which are returned rather
 * than logged so callers and tests can assert on data instead of spying on the console.
 *
 * @param input - The profile response, or either preference block on its own. Missing, `null` and
 *   partially populated payloads are all valid input. Whatever the payload does not supply comes
 *   from the locale; what it does supply is honoured only for the countries the backend has
 *   settings for, so a partial payload resolves entirely from the locale everywhere else.
 * @returns The resolved preferences with their signature, and every substitution made along the way.
 */
export const resolvePreferences = (
  input: PreferenceInput
): PreferenceResolution => {
  const issues: PreferenceIssue[] = [];

  const locale = resolveLocale(input, issues);
  const timeZone = resolveTimeZone(input, issues);

  const country = countryOf(locale);
  const trustBackend =
    country !== undefined && BACKEND_DEFAULT_COUNTRY_SET.has(country);

  const effective = input?.effectiveUserPreferences;
  const measurements = effective?.measurements;
  const formats = effective?.formats;
  const speaksLibrary = carriesLibraryVocabulary(effective);

  const localeSystem = deriveUnitSystem(locale);
  const librarySystem = readLibraryCode(
    UNIT_SYSTEMS,
    effective?.unitSystem,
    'effectiveUserPreferences.unitSystem',
    localeSystem,
    issues
  );
  const deprecatedSystem =
    !librarySystem && trustBackend
      ? readMappedCode(
          SYSTEM_BY_DEPRECATED_CODE,
          measurements?.unitSystem,
          'effectiveUserPreferences.measurements.unitSystem'
        )
      : {};
  const backendUnitSystem = librarySystem ?? deprecatedSystem.value;

  const unitSystem = backendUnitSystem ?? localeSystem;
  collect(issues, deprecatedSystem.problem, unitSystem);

  // Only worth saying when a code was actually disregarded, and only when nothing else answered:
  // an empty measurements/formats object is truthy but carries nothing, and a payload the library
  // already resolved has not lost anything by the older block being passed over.
  if (
    !trustBackend &&
    !speaksLibrary &&
    blockCarriesACode(measurements, formats)
  ) {
    issues.push({
      field: 'effectiveUserPreferences',
      received: locale,
      reason: ISSUE_REASONS.BACKEND_HAS_NO_LOCALE_DEFAULTS,
      resolvedTo: locale,
    });
  }

  const values: PreferenceValues = {
    locale,
    timeZone,
    // A label, not an instruction: per-field codes have already won above, so a user with miles and
    // Celsius reports as US_CUSTOMARY while still reading Celsius.
    unitSystem,
    units: Object.freeze(
      resolveUnits(
        measurements,
        effective?.units,
        locale,
        backendUnitSystem,
        trustBackend,
        issues
      )
    ),
    formats: Object.freeze(
      resolveFormats(formats, effective, locale, trustBackend, issues)
    ),
  };

  const preferences = Object.freeze({
    ...values,
    signature: signatureOf(values),
  });

  return { preferences, issues: Object.freeze(issues) };
};
